import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";

export default function CoachDashboard() {
  const [me, setMe] = useState(null);
  const [athletes, setAthletes] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviting, setInviting] = useState(false);

  const load = async () => {
    try {
      const [m, a, iv] = await Promise.all([
        api.get("/coach/me"),
        api.get("/coach/athletes"),
        api.get("/coach/invites"),
      ]);
      setMe(m.data); setAthletes(a.data.athletes || []); setInvites(iv.data.invites || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const copyCode = () => { navigator.clipboard.writeText(me.coach_code); toast.success("Coach code copied"); };
  const unlink = async (athlete_id) => {
    if (!window.confirm("Remove this athlete from your roster?")) return;
    await api.delete(`/coach/athletes/${athlete_id}`);
    toast.success("Athlete removed");
    load();
  };

  const sendInvite = async () => {
    if (!inviteEmail.trim()) return;
    setInviting(true);
    try {
      const r = await api.post("/coach/invites", { email: inviteEmail.trim() });
      if (r.data.email_sent) toast.success(`Invite emailed to ${inviteEmail.trim()}`);
      else toast.error(r.data.email_error?.includes("testing email") ? "Resend test mode: email not sent — share the invite link manually" : (r.data.email_error || "Email failed"));
      setInviteEmail("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create invite"); }
    finally { setInviting(false); }
  };

  const cancelInvite = async (id) => {
    await api.delete(`/coach/invites/${id}`);
    toast.success("Invite canceled");
    load();
  };

  if (loading) return <div className="text-neutral-400">Loading…</div>;
  if (!me) return <div className="text-neutral-400">Not a coach account.</div>;

  const full = me.linked_count >= me.athlete_limit;
  const pendingInvites = invites.filter((i) => i.status === "pending");

  return (
    <div>
      <div className="rs-underline mb-3" />
      <h1 className="font-display-heavy text-4xl">COACH DASHBOARD</h1>
      <p className="text-neutral-400 mt-2">Your athletes, all in one place.</p>

      <div className="mt-8 grid md:grid-cols-3 gap-4">
        <div className="border border-neutral-800 rounded-md bg-neutral-900 p-6 md:col-span-2">
          <div className="uppercase text-xs text-neutral-400 font-display tracking-widest">Invite an athlete</div>
          <p className="text-neutral-400 text-sm mt-1">Send a signup link straight to their inbox.</p>
          <div className="mt-3 flex gap-2">
            <Input data-testid="invite-email-input" type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="athlete@email.com" className="h-11 bg-neutral-950 border-neutral-700 flex-1" />
            <Button data-testid="send-invite-btn" onClick={sendInvite} disabled={inviting || !inviteEmail} className="h-11 bg-red-600 hover:bg-red-700 font-semibold">
              {inviting ? "Sending…" : "Send invite"}
            </Button>
          </div>
          <div className="mt-4 pt-4 border-t border-neutral-800">
            <div className="uppercase text-xs text-neutral-400 font-display tracking-widest">Or share your code</div>
            <div className="mt-2 flex items-center gap-3">
              <code data-testid="coach-code" className="font-display-heavy text-2xl text-red-500 tracking-widest">{me.coach_code}</code>
              <Button data-testid="copy-coach-code" onClick={copyCode} variant="outline" className="h-10 border-neutral-700 text-white hover:bg-neutral-800">Copy</Button>
            </div>
          </div>
        </div>
        <div className="border border-neutral-800 rounded-md bg-neutral-900 p-6">
          <div className="uppercase text-xs text-neutral-400 font-display tracking-widest">Roster</div>
          <div className="font-display-heavy text-5xl mt-2">{me.linked_count}<span className="text-neutral-500 text-2xl">/{me.athlete_limit}</span></div>
          <p className="text-neutral-400 mt-2 text-sm">{me.has_team_plan ? "Team plan active." : "Upgrade to Team Plan for 25 athletes."}</p>
          {!me.has_team_plan && <Link to="/pricing"><Button data-testid="coach-upgrade-btn" variant="outline" className="mt-3 h-10 border-neutral-700 text-white hover:bg-neutral-800">See team plan</Button></Link>}
        </div>
      </div>

      {full && !me.has_team_plan && (
        <div className="mt-6 border border-yellow-800 bg-yellow-950/40 text-yellow-300 rounded-md p-4">
          Your roster is full. Upgrade to Team Plan to link up to 25 athletes.
        </div>
      )}

      {pendingInvites.length > 0 && (
        <div className="mt-10">
          <div className="rs-underline mb-3" />
          <h2 className="font-display-heavy text-2xl">PENDING INVITES</h2>
          <div className="mt-4 space-y-2">
            {pendingInvites.map((iv) => (
              <div key={iv.id} className="flex items-center justify-between gap-3 border border-neutral-800 rounded-md bg-neutral-900 p-4">
                <div>
                  <div className="font-semibold">{iv.athlete_email}</div>
                  <div className="text-xs text-neutral-500">Sent {new Date(iv.created_at).toLocaleDateString()} · expires {new Date(iv.expires_at).toLocaleDateString()}</div>
                </div>
                <button data-testid={`cancel-invite-${iv.id}`} onClick={() => cancelInvite(iv.id)} className="text-xs text-neutral-500 hover:text-red-500 uppercase tracking-widest">Cancel</button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-10">
        <div className="rs-underline mb-3" />
        <h2 className="font-display-heavy text-2xl">YOUR ATHLETES</h2>
        {athletes.length === 0 ? (
          <div className="mt-4 border border-dashed border-neutral-800 rounded-md p-8 text-center text-neutral-400">
            Nobody linked yet. Send an invite or share your code above.
          </div>
        ) : (
          <div className="mt-4 grid md:grid-cols-2 gap-4">
            {athletes.map((a) => (
              <div data-testid={`coach-athlete-${a.id}`} key={a.id} className="border border-neutral-800 rounded-md bg-neutral-900 p-5">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-display-heavy text-xl">{a.name}</div>
                    <div className="text-neutral-400 text-sm">{a.email}</div>
                  </div>
                  <span className="text-xs uppercase font-display font-bold tracking-widest px-2 py-1 rounded bg-red-600/20 text-red-400">{a.active_focus_count} focus</span>
                </div>
                {a.latest_analysis && (
                  <div className="mt-3 text-sm text-neutral-300">
                    <span className="text-neutral-500">Latest:</span> {a.latest_analysis.sport} · {new Date(a.latest_analysis.created_at).toLocaleDateString()}
                  </div>
                )}
                <div className="mt-4 flex flex-wrap gap-2">
                  <Link to={`/coach/athletes/${a.id}`}><Button data-testid={`view-athlete-${a.id}`} className="h-10 bg-red-600 hover:bg-red-700 font-semibold">Open plan</Button></Link>
                  <Button data-testid={`unlink-athlete-${a.id}`} onClick={() => unlink(a.id)} variant="ghost" className="h-10 text-neutral-400 hover:text-white hover:bg-neutral-800">Unlink</Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
